import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../common/SafeIcon';
import { FiPlus, FiEdit2, FiX, FiSave, FiDatabase, FiUpload, FiDownload, FiLoader, FiCheck, FiAlertCircle, FiLink, FiTrash2 } from 'react-icons/fi';

const TABS = [
  { key: 'manufacturers', label: 'Manufacturers' },
  { key: 'importers', label: 'Importers' },
  { key: 'types', label: 'Firearm Types' },
  { key: 'calibers', label: 'Calibers' },
  { key: 'models', label: 'Models' },
];

const SAMPLE_CSV = {
  manufacturers: 'name,city,state,country,is_domestic\nSmith & Wesson,Springfield,MA,US,true\nGlock,Smyrna,GA,US,true\nCZ,Uhersky Brod,,CZ,false\nBersa,Ramos Mejia,,AR,false',
  importers: 'name,city,state\nCZ-USA,Kansas City,KS\nEagle Imports,Wanamassa,NJ\nCentury Arms,Delray Beach,FL',
  types: 'name\nPistol\nRevolver\nRifle\nShotgun\nReceiver',
  calibers: 'name\n9mm\n.45 ACP\n.223 Rem\n5.56 NATO\n.308 Win\n12 Gauge',
  models: 'name,manufacturer\nModel 10,Smith & Wesson\nG19,Glock\nP320,Sig Sauer\nMark IV,Ruger\n870 Express,Remington',
};

// Tabs that have extra fields beyond just "name"
const HAS_EXTRA_FIELDS = ['manufacturers', 'importers'];

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(line => line.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  const parseLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') {
          current += '"';
          i++;
        } else if (ch === '"') {
          inQuotes = false;
        } else {
          current += ch;
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
        } else if (ch === ',') {
          result.push(current.trim());
          current = '';
        } else {
          current += ch;
        }
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseLine(lines[0]).map(h => h.toLowerCase());
  const rows = lines.slice(1).map(parseLine);
  return { headers, rows };
}

export default function Lookups() {
  const { tenant } = useAuth();
  const [activeTab, setActiveTab] = useState('manufacturers');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editExtras, setEditExtras] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [newExtras, setNewExtras] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // For models tab: manufacturer selection
  const [manufacturers, setManufacturers] = useState([]);
  const [selectedMfr, setSelectedMfr] = useState('');

  // For manufacturer-importer linking
  const [linkedImporters, setLinkedImporters] = useState([]);
  const [importers, setImporters] = useState([]);
  const [linkImporterId, setLinkImporterId] = useState('');
  const [linkNotes, setLinkNotes] = useState('');
  const [linkingSaving, setLinkingSaving] = useState(false);
  const [addingNewLinkImporter, setAddingNewLinkImporter] = useState(false);
  const [newLinkImporterName, setNewLinkImporterName] = useState('');
  const [newLinkImporterCity, setNewLinkImporterCity] = useState('');
  const [newLinkImporterState, setNewLinkImporterState] = useState('');

  // Import state
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const fetchItems = async () => {
    if (!tenant) return;
    setLoading(true);
    try {
      const filters = activeTab === 'models' && selectedMfr ? { manufacturer_id: selectedMfr } : {};
      const data = await api.getLookups(activeTab, tenant.id, filters);
      setItems(data);
    } catch (err) {
      console.error(`Error fetching ${activeTab}:`, err);
    } finally {
      setLoading(false);
    }
  };

  const fetchManufacturers = async () => {
    if (!tenant) return;
    try {
      const data = await api.getLookups('manufacturers', tenant.id);
      setManufacturers(data);
    } catch (err) {
      console.error('Error fetching manufacturers:', err);
    }
  };

  const fetchImporters = async () => {
    if (!tenant) return;
    try {
      const data = await api.getLookups('importers', tenant.id);
      setImporters(data);
    } catch (err) {
      console.error('Error fetching importers:', err);
    }
  };

  const fetchLinkedImporters = async (mfrId) => {
    try {
      const data = await api.getManufacturerImporters(mfrId);
      setLinkedImporters(data);
    } catch (err) {
      console.error('Error fetching linked importers:', err);
      setLinkedImporters([]);
    }
  };

  useEffect(() => {
    fetchItems();
    if (activeTab === 'models') {
      fetchManufacturers();
    }
  }, [tenant, activeTab, selectedMfr]);

  // Reset state when switching tabs
  useEffect(() => {
    setEditingId(null);
    setShowAdd(false);
    setNewValue('');
    setNewExtras({});
    setSearchTerm('');
    setSelectedMfr('');
    setImportResult(null);
    setLinkedImporters([]);
  }, [activeTab]);

  // When editing a non-domestic manufacturer, load linked importers and available importers
  useEffect(() => {
    if (editingId && activeTab === 'manufacturers') {
      const item = items.find(i => i.id === editingId);
      if (item && item.is_domestic === false) {
        fetchLinkedImporters(editingId);
        fetchImporters();
      } else {
        setLinkedImporters([]);
      }
    }
  }, [editingId, editExtras.is_domestic]);

  const filtered = items.filter(item =>
    (item.name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const handleAdd = async () => {
    if (!newValue.trim()) return;
    setSaving(true);
    try {
      let extras = {};
      if (activeTab === 'models' && selectedMfr) {
        extras = { manufacturer_id: selectedMfr };
      } else if (activeTab === 'manufacturers') {
        extras = {
          city: newExtras.city?.trim() || null,
          state: newExtras.state?.trim() || null,
          country: newExtras.country?.trim() || 'US',
          is_domestic: newExtras.is_domestic !== false
        };
      } else if (activeTab === 'importers') {
        extras = {
          city: newExtras.city?.trim() || null,
          state: newExtras.state?.trim() || null
        };
      }
      await api.createLookup(activeTab, tenant.id, newValue.trim(), extras);
      setShowAdd(false);
      setNewValue('');
      setNewExtras({});
      fetchItems();
    } catch (err) {
      alert(`Error creating item: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!editValue.trim()) return;
    setSaving(true);
    try {
      let extras = {};
      if (activeTab === 'manufacturers') {
        extras = {
          city: editExtras.city?.trim() || null,
          state: editExtras.state?.trim() || null,
          country: editExtras.country?.trim() || 'US',
          is_domestic: editExtras.is_domestic !== false
        };
      } else if (activeTab === 'importers') {
        extras = {
          city: editExtras.city?.trim() || null,
          state: editExtras.state?.trim() || null
        };
      }
      await api.updateLookup(activeTab, editingId, editValue.trim(), extras);
      setEditingId(null);
      setEditValue('');
      setEditExtras({});
      setLinkedImporters([]);
      fetchItems();
    } catch (err) {
      alert(`Error updating item: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditValue(item.name);
    if (activeTab === 'manufacturers') {
      setEditExtras({
        city: item.city || '',
        state: item.state || '',
        country: item.country || 'US',
        is_domestic: item.is_domestic !== false
      });
    } else if (activeTab === 'importers') {
      setEditExtras({
        city: item.city || '',
        state: item.state || ''
      });
    } else {
      setEditExtras({});
    }
  };

  const handleLinkImporter = async () => {
    if (!linkImporterId || !editingId) return;
    setLinkingSaving(true);
    try {
      await api.linkManufacturerImporter(tenant.id, editingId, linkImporterId, linkNotes.trim());
      setLinkImporterId('');
      setLinkNotes('');
      fetchLinkedImporters(editingId);
    } catch (err) {
      alert(`Error linking importer: ${err.message}`);
    } finally {
      setLinkingSaving(false);
    }
  };

  const handleUnlinkImporter = async (linkId) => {
    try {
      await api.unlinkManufacturerImporter(linkId);
      fetchLinkedImporters(editingId);
    } catch (err) {
      alert(`Error unlinking importer: ${err.message}`);
    }
  };

  const handleCreateAndLinkImporter = async () => {
    if (!newLinkImporterName.trim() || !editingId) return;
    setLinkingSaving(true);
    try {
      const created = await api.createLookup('importers', tenant.id, newLinkImporterName.trim(), {
        city: newLinkImporterCity.trim() || null,
        state: newLinkImporterState.trim() || null,
      });
      setImporters(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      await api.linkManufacturerImporter(tenant.id, editingId, created.id, linkNotes.trim());
      setNewLinkImporterName('');
      setNewLinkImporterCity('');
      setNewLinkImporterState('');
      setLinkNotes('');
      setAddingNewLinkImporter(false);
      fetchLinkedImporters(editingId);
    } catch (err) {
      alert(`Error creating importer: ${err.message || 'Unknown error'}`);
    } finally {
      setLinkingSaving(false);
    }
  };

  const downloadSampleCSV = () => {
    const csv = SAMPLE_CSV[activeTab];
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sample-${activeTab}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = '';
    setImporting(true);
    setImportResult(null);

    try {
      const text = await file.text();
      const { headers, rows } = parseCSV(text);

      if (rows.length === 0) {
        setImportResult({ success: 0, skipped: 0, errors: ['CSV file has no data rows.'] });
        setImporting(false);
        return;
      }

      const nameIdx = headers.indexOf('name');
      if (nameIdx === -1) {
        setImportResult({ success: 0, skipped: 0, errors: ['CSV must have a "name" column header.'] });
        setImporting(false);
        return;
      }

      // For models, we need a manufacturer column to look up manufacturer_id
      let mfrIdx = -1;
      let mfrLookup = {};
      if (activeTab === 'models') {
        mfrIdx = headers.indexOf('manufacturer');
        if (mfrIdx === -1) {
          setImportResult({ success: 0, skipped: 0, errors: ['Models CSV must have a "manufacturer" column.'] });
          setImporting(false);
          return;
        }
        const mfrList = await api.getLookups('manufacturers', tenant.id);
        mfrList.forEach(m => { mfrLookup[m.name.toLowerCase()] = m.id; });
      }

      // Extra column indices for manufacturers/importers
      const cityIdx = headers.indexOf('city');
      const stateIdx = headers.indexOf('state');
      const countryIdx = headers.indexOf('country');
      const isDomesticIdx = headers.indexOf('is_domestic');

      // Get existing items to skip duplicates
      const existingItems = await api.getLookups(activeTab, tenant.id, {});
      const existingNames = new Set(existingItems.map(i => i.name.toLowerCase()));

      let success = 0;
      let skipped = 0;
      const errors = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const name = row[nameIdx]?.trim();
        if (!name) {
          skipped++;
          continue;
        }

        if (existingNames.has(name.toLowerCase())) {
          skipped++;
          continue;
        }

        try {
          let extras = {};
          if (activeTab === 'models') {
            const mfrName = row[mfrIdx]?.trim();
            if (!mfrName) {
              errors.push(`Row ${i + 2}: Missing manufacturer name.`);
              continue;
            }
            const mfrId = mfrLookup[mfrName.toLowerCase()];
            if (!mfrId) {
              errors.push(`Row ${i + 2}: Manufacturer "${mfrName}" not found. Add it first.`);
              continue;
            }
            extras = { manufacturer_id: mfrId };
          } else if (activeTab === 'manufacturers') {
            extras = {
              city: cityIdx >= 0 ? row[cityIdx]?.trim() || null : null,
              state: stateIdx >= 0 ? row[stateIdx]?.trim() || null : null,
              country: countryIdx >= 0 ? row[countryIdx]?.trim() || 'US' : 'US',
              is_domestic: isDomesticIdx >= 0 ? row[isDomesticIdx]?.trim().toLowerCase() !== 'false' : true
            };
          } else if (activeTab === 'importers') {
            extras = {
              city: cityIdx >= 0 ? row[cityIdx]?.trim() || null : null,
              state: stateIdx >= 0 ? row[stateIdx]?.trim() || null : null
            };
          }

          await api.createLookup(activeTab, tenant.id, name, extras);
          existingNames.add(name.toLowerCase());
          success++;
        } catch (err) {
          errors.push(`Row ${i + 2} ("${name}"): ${err.message}`);
        }
      }

      setImportResult({ success, skipped, errors });
      fetchItems();
      if (activeTab === 'manufacturers') {
        fetchManufacturers();
      }
    } catch (err) {
      setImportResult({ success: 0, skipped: 0, errors: [`Failed to read file: ${err.message}`] });
    } finally {
      setImporting(false);
    }
  };

  const tabLabel = TABS.find(t => t.key === activeTab)?.label || activeTab;
  const isManufacturers = activeTab === 'manufacturers';
  const isImporters = activeTab === 'importers';
  const hasExtraFields = HAS_EXTRA_FIELDS.includes(activeTab);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Manage Lookups</h2>
        <p className="text-sm text-slate-500">Edit manufacturers, importers, firearm types, calibers, and models.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
              activeTab === tab.key
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Input
            placeholder={`Search ${tabLabel.toLowerCase()}...`}
            className="w-[250px]"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {activeTab === 'models' && (
            <select
              value={selectedMfr}
              onChange={(e) => setSelectedMfr(e.target.value)}
              className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-900 dark:border-slate-700 dark:text-white"
            >
              <option value="">All Manufacturers</option>
              {manufacturers.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={downloadSampleCSV} icon={FiDownload}>
            Sample CSV
          </Button>
          <Button
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            icon={importing ? FiLoader : FiUpload}
            disabled={importing}
          >
            {importing ? 'Importing...' : 'Import CSV'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleImportCSV}
          />
          <Button onClick={() => setShowAdd(true)} icon={FiPlus} disabled={showAdd || (activeTab === 'models' && !selectedMfr)}>
            Add {tabLabel.replace(/s$/, '')}
          </Button>
        </div>
      </div>

      {activeTab === 'models' && !selectedMfr && (
        <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3">
          Select a manufacturer to filter models. A manufacturer is required to add a new model.
        </div>
      )}

      {/* Import result banner */}
      {importResult && (
        <div className={`rounded-lg px-4 py-3 border text-sm ${
          importResult.errors.length > 0
            ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
            : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
        }`}>
          <div className="flex items-start gap-3">
            <SafeIcon
              icon={importResult.errors.length > 0 ? FiAlertCircle : FiCheck}
              className={`h-5 w-5 mt-0.5 shrink-0 ${
                importResult.errors.length > 0 ? 'text-amber-600' : 'text-green-600'
              }`}
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-900 dark:text-white">
                Import complete: {importResult.success} added, {importResult.skipped} skipped (duplicates/empty).
              </p>
              {importResult.errors.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-400">
                  {importResult.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
            <button
              onClick={() => setImportResult(null)}
              className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              <SafeIcon icon={FiX} className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Add new item */}
      {showAdd && (
        <Card className="border-primary-200 dark:border-primary-900 shadow-lg">
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Input
                    label={`New ${tabLabel.replace(/s$/, '')} Name`}
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    placeholder={`Enter ${tabLabel.replace(/s$/, '').toLowerCase()} name...`}
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter' && !hasExtraFields) handleAdd(); }}
                  />
                </div>
                {!hasExtraFields && (
                  <>
                    <Button onClick={handleAdd} isLoading={saving} disabled={!newValue.trim()} icon={FiPlus}>Add</Button>
                    <Button variant="secondary" onClick={() => { setShowAdd(false); setNewValue(''); setNewExtras({}); }}>Cancel</Button>
                  </>
                )}
              </div>
              {isManufacturers && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Input
                      label="City"
                      value={newExtras.city || ''}
                      onChange={(e) => setNewExtras({...newExtras, city: e.target.value})}
                      placeholder="e.g. Springfield"
                    />
                    <Input
                      label="State"
                      value={newExtras.state || ''}
                      onChange={(e) => setNewExtras({...newExtras, state: e.target.value})}
                      placeholder="e.g. MA"
                    />
                    <Input
                      label="Country"
                      value={newExtras.country ?? 'US'}
                      onChange={(e) => setNewExtras({...newExtras, country: e.target.value})}
                      placeholder="e.g. US"
                    />
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Domestic</label>
                      <label className="flex items-center gap-2 h-10 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newExtras.is_domestic !== false}
                          onChange={(e) => setNewExtras({...newExtras, is_domestic: e.target.checked})}
                          className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-slate-600 dark:text-slate-400">US Manufacturer</span>
                      </label>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={() => { setShowAdd(false); setNewValue(''); setNewExtras({}); }}>Cancel</Button>
                    <Button onClick={handleAdd} isLoading={saving} disabled={!newValue.trim()} icon={FiPlus}>Add Manufacturer</Button>
                  </div>
                </>
              )}
              {isImporters && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="City"
                      value={newExtras.city || ''}
                      onChange={(e) => setNewExtras({...newExtras, city: e.target.value})}
                      placeholder="e.g. Kansas City"
                    />
                    <Input
                      label="State"
                      value={newExtras.state || ''}
                      onChange={(e) => setNewExtras({...newExtras, state: e.target.value})}
                      placeholder="e.g. KS"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={() => { setShowAdd(false); setNewValue(''); setNewExtras({}); }}>Cancel</Button>
                    <Button onClick={handleAdd} isLoading={saving} disabled={!newValue.trim()} icon={FiPlus}>Add Importer</Button>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Items list */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 bg-white dark:bg-slate-900 rounded-lg border border-dashed border-slate-300 dark:border-slate-800">
          <SafeIcon icon={FiDatabase} className="h-12 w-12 text-slate-300 dark:text-slate-700 mb-3" />
          <p className="text-slate-500">
            {searchTerm ? `No ${tabLabel.toLowerCase()} match your search.` : `No ${tabLabel.toLowerCase()} yet.`}
          </p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-6 py-3">Name</th>
                  {isManufacturers && <th className="px-6 py-3">City</th>}
                  {isManufacturers && <th className="px-6 py-3">State</th>}
                  {isManufacturers && <th className="px-6 py-3">Country</th>}
                  {isManufacturers && <th className="px-6 py-3">Domestic</th>}
                  {isImporters && <th className="px-6 py-3">City</th>}
                  {isImporters && <th className="px-6 py-3">State</th>}
                  {activeTab === 'models' && <th className="px-6 py-3">Manufacturer</th>}
                  <th className="px-6 py-3 text-right w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {filtered.map((item) => (
                  <React.Fragment key={item.id}>
                    <tr className="bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                      <td className="px-6 py-3">
                        {editingId === item.id ? (
                          <input
                            autoFocus
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !hasExtraFields) handleSave();
                              if (e.key === 'Escape') { setEditingId(null); setLinkedImporters([]); }
                            }}
                            className="h-8 w-full max-w-xs rounded-md border border-primary-300 bg-white px-2 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-800 dark:border-primary-700 dark:text-white"
                          />
                        ) : (
                          <span className="text-slate-900 dark:text-white font-medium">{item.name}</span>
                        )}
                      </td>
                      {isManufacturers && (
                        <td className="px-6 py-3 text-slate-500">
                          {editingId === item.id ? (
                            <input value={editExtras.city || ''} onChange={(e) => setEditExtras({...editExtras, city: e.target.value})} placeholder="City" className="h-8 w-full max-w-[120px] rounded-md border border-primary-300 bg-white px-2 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-800 dark:border-primary-700 dark:text-white" />
                          ) : (item.city || '-')}
                        </td>
                      )}
                      {isManufacturers && (
                        <td className="px-6 py-3 text-slate-500">
                          {editingId === item.id ? (
                            <input value={editExtras.state || ''} onChange={(e) => setEditExtras({...editExtras, state: e.target.value})} placeholder="State" className="h-8 w-full max-w-[80px] rounded-md border border-primary-300 bg-white px-2 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-800 dark:border-primary-700 dark:text-white" />
                          ) : (item.state || '-')}
                        </td>
                      )}
                      {isManufacturers && (
                        <td className="px-6 py-3 text-slate-500">
                          {editingId === item.id ? (
                            <input value={editExtras.country ?? 'US'} onChange={(e) => setEditExtras({...editExtras, country: e.target.value})} placeholder="Country" className="h-8 w-full max-w-[80px] rounded-md border border-primary-300 bg-white px-2 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-800 dark:border-primary-700 dark:text-white" />
                          ) : (item.country || 'US')}
                        </td>
                      )}
                      {isManufacturers && (
                        <td className="px-6 py-3">
                          {editingId === item.id ? (
                            <input type="checkbox" checked={editExtras.is_domestic !== false} onChange={(e) => setEditExtras({...editExtras, is_domestic: e.target.checked})} className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500" />
                          ) : (
                            item.is_domestic !== false
                              ? <Badge variant="success">Yes</Badge>
                              : <Badge variant="warning">No</Badge>
                          )}
                        </td>
                      )}
                      {isImporters && (
                        <td className="px-6 py-3 text-slate-500">
                          {editingId === item.id ? (
                            <input value={editExtras.city || ''} onChange={(e) => setEditExtras({...editExtras, city: e.target.value})} placeholder="City" className="h-8 w-full max-w-[120px] rounded-md border border-primary-300 bg-white px-2 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-800 dark:border-primary-700 dark:text-white" />
                          ) : (item.city || '-')}
                        </td>
                      )}
                      {isImporters && (
                        <td className="px-6 py-3 text-slate-500">
                          {editingId === item.id ? (
                            <input value={editExtras.state || ''} onChange={(e) => setEditExtras({...editExtras, state: e.target.value})} placeholder="State" className="h-8 w-full max-w-[80px] rounded-md border border-primary-300 bg-white px-2 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-800 dark:border-primary-700 dark:text-white" />
                          ) : (item.state || '-')}
                        </td>
                      )}
                      {activeTab === 'models' && (
                        <td className="px-6 py-3 text-slate-500">
                          {manufacturers.find(m => m.id === item.manufacturer_id)?.name || '-'}
                        </td>
                      )}
                      <td className="px-6 py-3 text-right">
                        {editingId === item.id ? (
                          <div className="inline-flex items-center gap-1">
                            <button
                              onClick={handleSave}
                              disabled={saving || !editValue.trim()}
                              className="inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                            >
                              <SafeIcon icon={FiSave} className="h-3.5 w-3.5" />
                              Save
                            </button>
                            <button
                              onClick={() => { setEditingId(null); setLinkedImporters([]); }}
                              className="inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors"
                            >
                              <SafeIcon icon={FiX} className="h-3.5 w-3.5" />
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEdit(item)}
                            className="inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors"
                          >
                            <SafeIcon icon={FiEdit2} className="h-3.5 w-3.5" />
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                    {/* Linked Importers panel for non-domestic manufacturers in edit mode */}
                    {editingId === item.id && isManufacturers && editExtras.is_domestic === false && (
                      <tr className="bg-slate-50 dark:bg-slate-800/50">
                        <td colSpan="6" className="px-6 py-4">
                          <div className="space-y-3">
                            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                              <SafeIcon icon={FiLink} className="h-4 w-4" />
                              Linked US Importers
                            </h4>
                            {linkedImporters.length === 0 ? (
                              <p className="text-sm text-slate-500">No importers linked to this manufacturer yet.</p>
                            ) : (
                              <div className="space-y-1">
                                {linkedImporters.map((link) => (
                                  <div key={link.id} className="flex items-center justify-between gap-2 px-3 py-2 bg-white dark:bg-slate-900 rounded-md border border-slate-200 dark:border-slate-700">
                                    <div className="text-sm">
                                      <span className="font-medium text-slate-900 dark:text-white">{link.importer?.name}</span>
                                      {link.importer?.city && link.importer?.state && (
                                        <span className="text-slate-500"> — {link.importer.city}, {link.importer.state}</span>
                                      )}
                                      {link.notes && (
                                        <span className="text-xs text-slate-400 ml-2">({link.notes})</span>
                                      )}
                                    </div>
                                    <button
                                      onClick={() => handleUnlinkImporter(link.id)}
                                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                                      title="Unlink importer"
                                    >
                                      <SafeIcon icon={FiTrash2} className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            {!addingNewLinkImporter ? (
                              <div className="space-y-2 pt-1">
                                <div className="flex items-end gap-2">
                                  <div className="flex-1 max-w-xs">
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Link Importer</label>
                                    <select
                                      value={linkImporterId}
                                      onChange={(e) => setLinkImporterId(e.target.value)}
                                      className="h-8 w-full rounded-md border border-slate-300 bg-white px-2 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                                    >
                                      <option value="">Select importer...</option>
                                      {importers
                                        .filter(imp => !linkedImporters.some(l => l.importer?.id === imp.id))
                                        .map((imp) => (
                                          <option key={imp.id} value={imp.id}>
                                            {imp.name}{imp.city && imp.state ? ` — ${imp.city}, ${imp.state}` : ''}
                                          </option>
                                        ))
                                      }
                                    </select>
                                  </div>
                                  <div className="flex-1 max-w-xs">
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Notes (optional)</label>
                                    <input
                                      value={linkNotes}
                                      onChange={(e) => setLinkNotes(e.target.value)}
                                      placeholder='e.g. "pistols only"'
                                      className="h-8 w-full rounded-md border border-slate-300 bg-white px-2 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                                    />
                                  </div>
                                  <button
                                    onClick={handleLinkImporter}
                                    disabled={!linkImporterId || linkingSaving}
                                    className="h-8 inline-flex items-center gap-1 rounded px-3 text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
                                  >
                                    <SafeIcon icon={FiLink} className="h-3.5 w-3.5" />
                                    Link
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setAddingNewLinkImporter(true)}
                                  className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                                >
                                  <SafeIcon icon={FiPlus} className="h-3 w-3" />
                                  Create new importer
                                </button>
                              </div>
                            ) : (
                              <div className="space-y-2 pt-1 rounded-md border border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/10 p-3">
                                <div className="flex items-center justify-between">
                                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">New Importer</label>
                                  <button type="button" onClick={() => { setAddingNewLinkImporter(false); setNewLinkImporterName(''); setNewLinkImporterCity(''); setNewLinkImporterState(''); }} className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">Cancel</button>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  <input
                                    autoFocus
                                    value={newLinkImporterName}
                                    onChange={(e) => setNewLinkImporterName(e.target.value)}
                                    placeholder="Importer name *"
                                    className="h-8 rounded-md border border-slate-300 bg-white px-2 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateAndLinkImporter(); } if (e.key === 'Escape') { setAddingNewLinkImporter(false); setNewLinkImporterName(''); setNewLinkImporterCity(''); setNewLinkImporterState(''); } }}
                                  />
                                  <input
                                    value={newLinkImporterCity}
                                    onChange={(e) => setNewLinkImporterCity(e.target.value)}
                                    placeholder="City"
                                    className="h-8 rounded-md border border-slate-300 bg-white px-2 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                                  />
                                  <input
                                    value={newLinkImporterState}
                                    onChange={(e) => setNewLinkImporterState(e.target.value)}
                                    placeholder="State"
                                    className="h-8 rounded-md border border-slate-300 bg-white px-2 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                                  />
                                </div>
                                <div className="flex items-end gap-2">
                                  <div className="flex-1">
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Notes (optional)</label>
                                    <input
                                      value={linkNotes}
                                      onChange={(e) => setLinkNotes(e.target.value)}
                                      placeholder='e.g. "pistols only"'
                                      className="h-8 w-full rounded-md border border-slate-300 bg-white px-2 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                                    />
                                  </div>
                                  <button
                                    onClick={handleCreateAndLinkImporter}
                                    disabled={!newLinkImporterName.trim() || linkingSaving}
                                    className="h-8 inline-flex items-center gap-1 rounded px-3 text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
                                  >
                                    <SafeIcon icon={FiPlus} className="h-3.5 w-3.5" />
                                    Create & Link
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <div className="text-xs text-slate-400 text-right">
        {filtered.length} {tabLabel.toLowerCase()}
      </div>
    </div>
  );
}
